import { type RecentSearch, TIME_OPTIONS } from "@/features/search/types";
import { isSameDay } from "@/lib/dates";

const DEFAULT_LOCATION_LABEL = "Anywhere";

const PLACEHOLDER_LOCATION_LABELS = new Set([
  DEFAULT_LOCATION_LABEL,
  "Near you",
  "All locations",
  "Map area",
]);
const LOCATION_MODES = new Set(["place", "viewport", "coords"]);

function isDefaultLocationLabel(location: string | null | undefined): boolean {
  if (!location?.trim()) return true;
  return PLACEHOLDER_LOCATION_LABELS.has(location.trim());
}

// fallow-ignore-next-line complexity
function normalizeRecentSearchForStorage(search: RecentSearch): RecentSearch {
  const normalized: RecentSearch = { ...search };

  if (isDefaultLocationLabel(search.location)) {
    delete normalized.location;
  } else if (search.location) {
    normalized.location = search.location.trim();
  }

  if (!search.q?.trim()) delete normalized.q;
  else normalized.q = search.q.trim();

  if (!search.category?.trim()) delete normalized.category;
  if (!search.categoryName?.trim()) delete normalized.categoryName;

  if (!search.when_date?.trim()) delete normalized.when_date;
  if (!search.when_time || search.when_time === "any") delete normalized.when_time;

  if (!search.bounds?.trim()) delete normalized.bounds;
  if (search.bounds?.trim()) normalized.bounds = search.bounds.trim();

  const inferredLocationMode =
    typeof search.location_mode === "string" && LOCATION_MODES.has(search.location_mode)
      ? search.location_mode
      : normalized.bounds
        ? "place"
        : normalized.lat != null && normalized.lng != null
          ? "coords"
          : undefined;

  if (inferredLocationMode) normalized.location_mode = inferredLocationMode;
  else delete normalized.location_mode;

  if (
    !normalized.location &&
    normalized.lat == null &&
    normalized.lng == null &&
    !normalized.bounds
  ) {
    delete normalized.location_mode;
  }

  return normalized;
}

function isMeaningfulRecentSearch(search: RecentSearch): boolean {
  const normalized = normalizeRecentSearchForStorage(search);

  if (normalized.q) return true;
  if (normalized.category || normalized.categoryName) return true;
  if (normalized.location) return true;
  if (normalized.lat != null || normalized.lng != null) return true;
  if (normalized.bounds) return true;
  if (normalized.when_date) return true;
  if (normalized.when_time) return true;

  return false;
}

function recentSearchDedupeKey(search: RecentSearch): string {
  const n = normalizeRecentSearchForStorage(search);
  return [
    n.q ?? "",
    n.category ?? "",
    n.location ?? "",
    n.lat ?? "",
    n.lng ?? "",
    n.bounds ?? "",
    n.location_mode ?? "",
    n.when_date ?? "",
    n.when_time ?? "",
  ].join("|");
}

function formatRecentSearchLabel(r: RecentSearch): string {
  const parts: string[] = [];

  if (r.categoryName) parts.push(r.categoryName);
  else if (r.q) parts.push(r.q);

  if (r.location && !isDefaultLocationLabel(r.location)) {
    parts.push(r.location);
  }

  if (r.when_date) {
    const d = new Date(`${r.when_date}T00:00:00`);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    if (isSameDay(d, today)) parts.push("Today");
    else if (isSameDay(d, tomorrow)) parts.push("Tomorrow");
    else parts.push(r.when_date);
  }

  const time = TIME_OPTIONS.find((option) => option.value === r.when_time);
  if (time && time.value !== "any") parts.push(time.label);

  return parts.join(" · ");
}

export {
  DEFAULT_LOCATION_LABEL,
  formatRecentSearchLabel,
  isDefaultLocationLabel,
  isMeaningfulRecentSearch,
  normalizeRecentSearchForStorage,
  recentSearchDedupeKey,
};
